import OrderError from 'errors/orderError'
import type { CruiseAmendmentOtpError } from './cruisePassengerAmendmentTypes'
import { isRecord } from 'lib/object/objectUtils'

export type CruiseAmendmentErrorKind = 'pending' | 'refunded' | 'payment' | 'unconfirmed'

export interface CruiseAmendmentError {
  code?: string
  params?: {
    failureCode?: string
    paymentParams?: Record<string, unknown>
    refundConfirmed?: boolean
    paymentFailure?: boolean
    providerApplied?: boolean
  }
}

export function getCruiseAmendmentTimeoutError(error: { message?: string }): CruiseAmendmentError | undefined {
  if (error.message !== 'Polling timed out. Please try again.') return undefined
  return { code: 'CRUISE_AMENDMENT_FAILED', params: { failureCode: 'ORDER_POLLING_TIMED_OUT' } }
}

export function handleCruiseAmendmentPollingError(error: unknown): never {
  const timeoutError = error instanceof OrderError && getCruiseAmendmentTimeoutError(error)
  if (timeoutError) {
    const amendmentError = new OrderError(error.message)
    amendmentError.code = timeoutError.code
    amendmentError.errors = [timeoutError]
    throw amendmentError
  }
  throw error
}

export function getCruiseAmendmentErrorKind(error: CruiseAmendmentError): CruiseAmendmentErrorKind | undefined {
  if (error.code !== 'CRUISE_AMENDMENT_FAILED') return undefined
  const outcome = error.params
  if (outcome?.failureCode === 'ORDER_POLLING_TIMED_OUT') return 'pending'
  if (outcome?.refundConfirmed && !outcome.providerApplied && !outcome.paymentFailure) return 'refunded'
  if (outcome?.providerApplied) return 'unconfirmed'
  if (outcome?.paymentFailure) {
    // Specific payment-provider failures keep the shared checkout messages.
    if (outcome.failureCode && !outcome.failureCode.startsWith('CRUISE_AMENDMENT_')) return undefined
    return 'payment'
  }
  return 'unconfirmed'
}

export function getCruiseAmendmentOtpError(error: unknown): CruiseAmendmentOtpError | undefined {
  if (!isRecord(error)) return undefined
  const code = error.code ?? (isRecord(error.error) ? error.error.code : undefined)
  if (code === 'otp_required' || code === 'no_phone_number') return code
  if (error.status === 429) return 'rate_limited'
  return undefined
}
